import React, { useEffect, useState } from 'react'
import { api } from '../../utils/api'
import DealLink from '../shared/DealLink.jsx'

// ---------------------------------------------------------------------------
// Detail drawer for a "Deliver in person" booking (spec_20260805).
//
// Deliberately NOT the courier DetailPanel: there is no carrier, no label, no
// FedEx machinery, and no arrival button (receiving's label scan IS the
// arrival marking). The one action is Change booking — a new day/slot written
// through E1, which Prism re-validates against availability and records with
// the operator + old->new in the audit trail.
// ---------------------------------------------------------------------------

function slotOptionLabel(slot) {
  return `${slot.start.slice(0, 5)}–${slot.end.slice(0, 5)}`
}

function WarehouseDropoffPanel({ shipment, onClose, onRebooked }) {
  const [contactInfo, setContactInfo] = useState(null)
  const [newDate, setNewDate] = useState(shipment.collection_date || '')
  const [daySlots, setDaySlots] = useState([])       // open slots for newDate
  const [dayWindow, setDayWindow] = useState(null)   // {start, end} of newDate
  // A mid-day closure splits the open hours; "anytime" would promise the
  // closed hour, so gap days force an hour choice (Prism refuses anytime
  // on them too — decision 2026-08-06).
  const [gapDay, setGapDay] = useState(false)
  const [dayOpen, setDayOpen] = useState(true)
  const [newSlot, setNewSlot] = useState(shipment.dropoff_slot_start || '')
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    api.get(`/api/deals/collections/${shipment.id}/contact`)
      .then(info => { if (alive) setContactInfo(info) })
      .catch(() => { if (alive) setContactInfo(null) })
    return () => { alive = false }
  }, [shipment.id])

  // Whenever the operator picks a date, fetch that day's availability so the
  // slot dropdown only offers what is genuinely bookable.
  useEffect(() => {
    if (!newDate) { setDaySlots([]); return }
    let alive = true
    setLoadingSlots(true)
    api.get(`/api/deals/warehouse-dropoff/availability?from=${newDate}&to=${newDate}`)
      .then(res => {
        if (!alive) return
        const day = (res.days || [])[0]
        setDayOpen(Boolean(day?.open))
        setDaySlots((day?.slots || []).filter(s => s.open))
        setDayWindow(day?.open_window || null)
        setGapDay((day?.open_ranges || []).length > 1)
      })
      .catch(() => { if (alive) { setDayOpen(false); setDaySlots([]); setDayWindow(null); setGapDay(false) } })
      .finally(() => { if (alive) setLoadingSlots(false) })
    return () => { alive = false }
  }, [newDate])

  async function rebook() {
    setSaving(true)
    setError(null)
    try {
      await api.post(`/api/deals/collections/${shipment.id}/rebook-dropoff`, {
        collection_date: newDate,
        dropoff_slot_start: newSlot || null,
      })
      // Reload the board rather than patching locally: if anything changed
      // underneath (a receive, a consolidation), fresh truth wins.
      onRebooked?.()
      onClose()
    } catch (e) {
      // Prism's 422 carries WHY (closed / started / horizon); the worker's
      // 409 means the booking was received/changed while this panel was open.
      setError(e.message || 'Rebook failed')
    } finally {
      setSaving(false)
    }
  }

  const unchanged = newDate === (shipment.collection_date || '')
    && (newSlot || null) === (shipment.dropoff_slot_start || null)

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 400,
      background: 'var(--color-bg-primary, white)', zIndex: 1000,
      boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', overflowY: 'auto',
      padding: 'var(--space-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Deliver in person</h3>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div style={{ fontSize: 13, marginBottom: 4, fontWeight: 600 }}>
        {shipment.dealname || 'Unnamed Deal'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 12 }}>
        <DealLink deal={shipment} />
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Customer</div>
        <div>{contactInfo?.firstName || shipment.customer_name || '—'}</div>
        <div style={{ color: 'var(--color-text-muted)' }}>{contactInfo?.email || shipment.seller_email || ''}</div>
        {contactInfo?.phone && <div style={{ color: 'var(--color-text-muted)' }}>{contactInfo.phone}</div>}
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Booked visit</div>
        <div>
          {shipment.collection_date || 'No date'}
          {' · '}
          {shipment.dropoff_slot_start
            ? `${shipment.dropoff_slot_start.slice(0, 5)} slot`
            : 'Anytime during open hours'}
        </div>
        <div style={{ color: 'var(--color-text-muted)', marginTop: 4, fontSize: 12 }}>
          No label, no carrier — the warehouse scan on arrival moves it to In Warehouse.
        </div>
      </div>

      <div className="card" style={{ padding: 12, fontSize: 13 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Change booking</div>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          New day
        </label>
        <input type="date" className="form-input" value={newDate}
          onChange={e => { setNewDate(e.target.value); setNewSlot('') }}
          style={{ marginBottom: 8, width: '100%' }} />

        <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          Time slot
        </label>
        <select className="form-input" value={newSlot} onChange={e => setNewSlot(e.target.value)}
          disabled={loadingSlots || !dayOpen} style={{ marginBottom: 8, width: '100%' }}>
          {/* Window-derived like the customer form's pill (spec decision #4):
              a trimmed day must read honestly, not pretend 9-5. A gap day
              (mid-day closure) offers no anytime at all — pick an hour. */}
          {gapDay ? (
            <option value="" disabled>Mid-day closure — pick an hour</option>
          ) : (
          <option value="">
            {dayWindow
              ? `Anytime between ${dayWindow.start.slice(0, 5)} and ${dayWindow.end.slice(0, 5)}`
              : 'Anytime during open hours'}
          </option>
          )}
          {daySlots.map(s => (
            <option key={s.start} value={s.start}>{slotOptionLabel(s)}</option>
          ))}
        </select>

        {!dayOpen && newDate && !loadingSlots && (
          <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>
            That day is closed — pick another.
          </div>
        )}
        {error && (
          <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{error}</div>
        )}

        <button className="btn btn-primary" style={{ width: '100%' }}
          disabled={saving || !newDate || !dayOpen || unchanged || (gapDay && !newSlot)}
          onClick={rebook}>
          {saving ? 'Rebooking…' : 'Rebook visit'}
        </button>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 11, marginTop: 6 }}>
          Prism re-checks availability on save and records who rebooked, from
          what, to what. Remember to tell the customer.
        </div>
      </div>
    </div>
  )
}

export default WarehouseDropoffPanel
